// Globally needed js:
if (typeof window !== 'undefined') {
  import('lazysizes');
  import('lazysizes/plugins/attrchange/ls.attrchange');
  import('picturefill');
  import('what-input');
}

import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { NextIntlClientProvider } from 'next-intl';
import Router, { useRouter } from 'next/router';

import { Provider } from 'react-redux';
// Accessibility: aria-hidden for content when modal is open
import Modal from 'react-modal';

// Annotations for react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'instantsearch.css/themes/reset.css';

// Redux
import track from '@helper/tracking';
import GlobalStyle from '../stylesheets/global';
import { wrapper } from '../redux/store';

// Progress Bar
import { startProgressBar, stopProgressBar } from '../helper/progessBar/progressBar';

// Api helper
import { buildDataUrl } from '../helper/api';

// Special Wrapper
import Theming from '../components/layout/theming/theming';
import AutoLogoutService from '../helper/autoLogoutService/autoLogoutService';
import RouterDebugger from '../helper/routerDebugger';

import translations from '../public/static/translations/common.json';

const Error = dynamic(() => import('../components/pages/error/error'));

// Router events
Router.events.on('routeChangeStart', () => {
  startProgressBar();
});
Router.events.on('routeChangeComplete', url => {
  track(['setCustomUrl', url]);
  track(['setDocumentTitle', document.querySelector('title')?.text]);
  if (document.referrer) {
    track(['setReferrerUrl', document.referrer]);
  }
  track(['enableLinkTracking']);
  track(['trackPageView']);
  stopProgressBar();
});
Router.events.on('routeChangeError', () => stopProgressBar());

// Accessibility: aria-hidden for content when modal is open
Modal.setAppElement('#__next');

const App = ({ Component, ...rest }) => {
  const { store, props } = wrapper.useWrappedStore(rest);
  const { pageProps } = props;

  const router = useRouter();
  const [trackedScrollPercentage, setTrackedScrollPercentage] = useState([]);

  const onScroll = () => {
    // https://stackoverflow.com/questions/2387136/cross-browser-method-to-determine-vertical-scroll-percentage-in-javascript#8028584
    const h = document.documentElement;
    const b = document.body;
    const st = 'scrollTop';
    const sh = 'scrollHeight';
    const percentScrolled = ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;

    const scrollPoint = [100, 75, 50, 25].find(percentage => percentage <= percentScrolled);
    if (scrollPoint) {
      if (!trackedScrollPercentage.includes(scrollPoint)) {
        setTrackedScrollPercentage(prev => [...prev, scrollPoint]);
        track(['trackEvent', 'Scroll', 'Scroll Depth', `${scrollPoint}%`]);
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', onScroll);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', onScroll);
      }
    };
  }, [trackedScrollPercentage]);

  useEffect(() => {
    const rescan = () => window.gomusEmbed?.rescan();
    rescan();
    router.events.on('routeChangeComplete', rescan);
    return () => router.events.off('routeChangeComplete', rescan);
  }, [router.events]);

  return (
    <Provider store={store}>
      <NextIntlClientProvider
        locale={router.locale || 'de'}
        timeZone="Europe/Vienna"
        messages={translations[router.locale] || translations.de}
      >
        <AutoLogoutService>
          <Theming>
            {process.env.NODE_ENV === 'development' && <RouterDebugger pageData={pageProps} />}
            <GlobalStyle />
            {pageProps.isError ? (
              <Error statusCode={pageProps?.statusCode || 404} lang={router.locale} />
            ) : (
              <Component {...pageProps} />
            )}
          </Theming>
        </AutoLogoutService>
      </NextIntlClientProvider>
    </Provider>
  );
};

const getAppProps = async ({ locale }) => {
  return fetch(buildDataUrl(`${locale || 'de'}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/bootstrap`))
    .then(res => res.json())
    .catch(err => console.error('app', err));
};

export default App;
export { getAppProps };
